/**
 * Error Boundary Components
 *
 * React error boundaries for graceful error handling in production.
 */

import { Component, type ReactNode } from "react";
import { handleReactError } from "../lib/error-tracking";
import { ENV_CONFIG } from "../config";
import { IconAlertTriangle, IconRefresh, IconHome } from "./Icons";

// =============================================================================
// TYPES
// =============================================================================

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback UI */
  fallback?: ReactNode;
  /** Called when error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Feature name for better error messages */
  feature?: string;
  /** Show retry button */
  showRetry?: boolean;
  /** Show home button */
  showHome?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

// =============================================================================
// ERROR BOUNDARY CLASS
// =============================================================================

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Report to Sentry
    handleReactError(error, errorInfo);

    // Log to console
    console.error(`[ErrorBoundary:${this.props.feature || "App"}]`, error, {
      componentStack: errorInfo.componentStack,
    });

    // Call custom handler
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    // For dynamic import errors, a full page reload is needed
    const isDynamicImportError =
      this.state.error?.message?.includes("dynamically imported module") ||
      this.state.error?.message?.includes("Failed to fetch");

    if (isDynamicImportError) {
      window.location.reload();
      return;
    }

    this.setState({ hasError: false, error: undefined });
  };

  handleHome = (): void => {
    window.location.href = "/";
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          feature={this.props.feature}
          onRetry={
            this.props.showRetry !== false ? this.handleRetry : undefined
          }
          onHome={this.props.showHome ? this.handleHome : undefined}
        />
      );
    }

    return this.props.children;
  }
}

// =============================================================================
// ERROR FALLBACK UI
// =============================================================================

interface ErrorFallbackProps {
  error?: Error;
  feature?: string;
  onRetry?: () => void;
  onHome?: () => void;
}

function ErrorFallback({
  error,
  feature,
  onRetry,
  onHome,
}: ErrorFallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] gap-4 p-6 text-center">
      <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
        <IconAlertTriangle className="w-8 h-8 text-red-500" />
      </div>

      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-slate-900">
          Что-то пошло не так
        </h2>
        <p className="text-sm text-slate-600 max-w-xs">
          {feature
            ? `Ошибка загрузки: ${feature}`
            : "Произошла непредвиденная ошибка"}
        </p>
        {ENV_CONFIG.isDev && error?.message && (
          <p className="text-xs text-red-600 font-mono mt-2 p-2 bg-red-50 rounded">
            {error.message}
          </p>
        )}
      </div>

      <div className="flex gap-2">
        {onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--color-accent)] text-white rounded-lg text-sm font-medium hover:bg-[var(--color-accent-hover)] transition-colors"
          >
            <IconRefresh className="w-4 h-4" />
            Повторить
          </button>
        )}
        {onHome && (
          <button
            onClick={onHome}
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            <IconHome className="w-4 h-4" />
            На главную
          </button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// FEATURE ERROR BOUNDARIES (pre-configured)
// =============================================================================

export function EventsErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary feature="Мероприятия" showRetry showHome>
      {children}
    </ErrorBoundary>
  );
}

export function OrdersErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary feature="Заказы" showRetry showHome>
      {children}
    </ErrorBoundary>
  );
}

export function TicketsErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary feature="Билеты" showRetry showHome>
      {children}
    </ErrorBoundary>
  );
}

export function PaymentErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary feature="Оплата" showRetry showHome>
      {children}
    </ErrorBoundary>
  );
}

export function AdminErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary feature="Админ-панель" showRetry showHome>
      {children}
    </ErrorBoundary>
  );
}

export function ProfileErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary feature="Профиль" showRetry showHome>
      {children}
    </ErrorBoundary>
  );
}

export function HomeErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary feature="Главная" showRetry showHome>
      {children}
    </ErrorBoundary>
  );
}

export function ScheduleErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary feature="Расписание" showRetry showHome>
      {children}
    </ErrorBoundary>
  );
}

export function ChurchErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary feature="Церковь" showRetry showHome>
      {children}
    </ErrorBoundary>
  );
}

export function PrayersErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary feature="Молитвы" showRetry showHome>
      {children}
    </ErrorBoundary>
  );
}

export function WorshipErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary feature="Богослужение" showRetry showHome>
      {children}
    </ErrorBoundary>
  );
}

export function HomeGroupErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary feature="Домашняя группа" showRetry showHome>
      {children}
    </ErrorBoundary>
  );
}
